import React, { useState, useEffect } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useLocation, useHistory } from 'react-router-dom';
import { useDataGrid } from 'hooks/useDataGrid';
import {
  DataGrid,
  GridSpecialColumnFields,
  useRightSideBar
} from '@p81-common/ui';

import {
  PageSubTitle,
  FilterButton,
  EmptyState,
  ClearButton,
  SelectDropDown,
  DatePicker,
  Loading,
  LinkBack
} from 'components/common/Elements/Elements';
import InputSearch from 'components/common/forms/InputSearch/InputSearch';
import Widgets from './parts/Widgets/Widgets';

import { postRequest } from 'utils/requests';
import {
  getRowHeight,
  onChangeExportButton,
  parsQueryForSave
} from 'utils/common';
import { exportConfigTeam } from './helpers/configurates';
import { PlatformTeamReq, createSaveTeamList } from './helpers/helpers';

import classes from './PlatformTeam.module.scss';
import {
  ApiResponse,
  Filter,
  HandleChangeRoleMenu,
  LastLoginInitial,
  TeamFilters
} from './helpers/types';
import {
  defaultFilters,
  masterDetailConfig,
  createTableConfig
} from './helpers/tableConfig';

import { initialTableShow } from 'utils/settings';
import createClasses from './styles';
import { ChangeUserRole } from './parts/ChangeUserRole';
import { access, resources, hasAccess } from 'utils/permissions';

const lastLoginInitial: LastLoginInitial = {
  startDate: null,
  endDate: null
};

const PlatformTeam = ({ customerId }: any) => {
  const styles = createClasses();

  const { setOpenSecondary, setComponentSecondaryDrawer } = useRightSideBar();

  const handleChangeRoleMenu: HandleChangeRoleMenu = user => {
    setOpenSecondary(true);
    setComponentSecondaryDrawer(
      <ChangeUserRole
        user={user}
        pagination={filter}
        filters={filters}
        updateTableData={setTableData}
      />
    );
  };

  const {
    handleTableSettingsMenuItemClick,
    handleSearchChangeCallback,
    handleChangeNumOfRows,
    setFilter,
    filter,
    onSortEnd,
    rowSelectionChanged,
    tableSettingsDisplay,
    numOfRowsOptions,
    tableSettingsChanged,
    tableData,
    setTableData,
    tableConfigDisplay,
    loaderTable,
    setLoaderTable,
    startFilter,
    setStartFilter,
    filterButton,
    handleFilterButton,
    onClearSearch,
    loader,
    setLoader
  } = useDataGrid(createTableConfig({ handleChangeRoleMenu }), defaultFilters);

  //filters and pagination
  const [filters, setFilters] = useState<Filter | {}>({
    fieldName: 'firstName',
    direction: 1,
    currentPage: 1,
    itemsPerPage: initialTableShow.itemsPerPage,
    search: '',
    role: [],
    groups: [],
    idp: [],
    lastVpnLogin: lastLoginInitial,
    lastAppLogin: lastLoginInitial
  });
  const [direction, setDirection] = useState(1);

  //data from api
  const [teamData, setTeamData] = useState<ApiResponse>();
  const [teamFilters, setTeamFilters] = useState<TeamFilters>();

  //other data

  const [dataConnection, setDataConnection] = useState(false);
  const [reset, setReset] = useState(true);
  const [triggerResetDatePicker, setTriggerResetDatePicker] = useState(false);

  const history = useHistory();
  const location = useLocation();

  const numberОfFilters = 4;
  const hasCustomerTeamReadAccess = hasAccess([
    resources.CUSTOMER_TEAM_SUPPORT,
    access.READ
  ]);

  exportConfigTeam.exportName = 'customer_' + customerId + '_team_';

  const { t } = useTranslation(['activity']);

  const handleSortable = (field: any) => {
    const newDirection = direction === 1 ? -1 : 1;
    setDirection(newDirection);
    setFilters({ ...filters, fieldName: field, direction: newDirection });
    setStartFilter(!startFilter);
  };
  const resetCurrentPage = () => {
    setFilters({ ...filters, currentPage: 1 });
  };

  const handleSearch = (e: any) => {
    if ((filters as Filter)?.search !== e.target.value) {
      resetCurrentPage();
      setFilters({ ...filters, search: e.target.value });
    }
  };

  useEffect((): (() => void) => {
    let cancelled = false;
    postRequest('/customer/platform/team/filters', {
      customerId: customerId
    })
      .then(res => {
        !cancelled && setTeamFilters(res.body);
      })
      .catch(err => {
        !cancelled && console.log(err);
      });
    return () => (cancelled = true);
  }, [customerId]);

  useEffect(() => {
    if (location.search) {
      const loadList = createSaveTeamList(location);
      setFilters(loadList);
    } else {
      history.replace(location.pathname);
      resetAllFilters();
    }
    // eslint-disable-next-line
  }, [filter]);

  const resetAllFilters = () => {
    setTriggerResetDatePicker(!triggerResetDatePicker);
    setFilters({
      fieldName: 'firstName',
      direction: 1,
      currentPage: 1,
      itemsPerPage: initialTableShow.itemsPerPage,
      search: '',
      role: [],
      groups: [],
      idp: [],
      lastVpnLogin: lastLoginInitial,
      lastAppLogin: lastLoginInitial
    });
    setFilter({
      ...filter,
      currentPage: initialTableShow.currentPage,
      itemsPerPage: initialTableShow.itemsPerPage
    });
    resetCurrentPage();
  };

  useEffect((): (() => void) => {
    let cancelled = false;

    setLoaderTable(true);
    const linkForRequest = `/customer/platform/team/list?page=${filter.currentPage}&limit=${filter.itemsPerPage}`;
    const REQ_BODY = PlatformTeamReq(filters, customerId);

    postRequest(linkForRequest, REQ_BODY)
      .then(res => {
        if (!cancelled) {
          setDataConnection(true);
          setTableData(res.body.data);
          setTeamData(res.body);
          setLoader(false);
          setLoaderTable(false);
        }
      })
      .catch(err => {
        if (!cancelled) {
          setDataConnection(false);
          setLoader(false);
          setLoaderTable(false);
          console.log(err);
        }
      });
    replaceHistory();
    setReset(false);
    return () => (cancelled = true);

    // eslint-disable-next-line
  }, [filters, customerId, filter]);
  const replaceHistory = () => {
    const query = `startApp=${
      (filters as Filter)?.lastAppLogin.startDate
    }&endApp=${(filters as Filter)?.lastAppLogin.endDate}&startVpn=${
      (filters as Filter)?.lastVpnLogin.startDate
    }&endVpn=${(filters as Filter)?.lastVpnLogin.endDate}&search=${
      (filters as Filter)?.search
    }&role=${(filters as Filter)?.role}&groups=${parsQueryForSave(
      (filters as Filter)?.groups
    )}&idp=${parsQueryForSave((filters as Filter)?.idp)}&fieldName=${
      (filters as Filter)?.fieldName || ''
    }&dir=${direction}&limit=${(filters as Filter)?.itemsPerPage}&page=${
      filter.currentPage
    }`;
    history.replace(`${location.pathname}?${query}`);
  };

  if (loader) {
    return <Loading />;
  }

  if (!dataConnection) {
    return <EmptyState message="connect" />;
  }
  return (
    <>
      <div className={classes.header}>
        <PageSubTitle title={t('activity:platform.TEAM')} />
        <LinkBack text="Platform" />
      </div>
      <div className={classes.filters_main}>
        <InputSearch
          placeholder={t('activity:filters.SEARCH_PLACEHOLDER_NE_ID')}
          value={(filters as Filter)?.search}
          onChange={handleSearch}
          onClearSearch={onClearSearch}
          disabled={loaderTable}
        />
        <FilterButton active={filterButton} onClick={handleFilterButton} />
      </div>
      <div
        className={cn(filterButton ? classes.filters : classes.filters_close)}
      >
        {!reset && (
          <>
            <SelectDropDown
              options={teamFilters?.role}
              input={{
                value: (filters as Filter)?.role,
                onChange: (v: string) => setFilters({ ...filters, role: v })
              }}
              label={t('activity:filters.ROLE')}
              multi
            />
            <SelectDropDown
              options={teamFilters?.groups}
              input={{
                value: (filters as Filter)?.groups,
                onChange: (v: string) => setFilters({ ...filters, groups: v })
              }}
              label={t('activity:filters.GROUPS')}
              multi
            />
            <SelectDropDown
              options={teamFilters?.idp}
              input={{
                value: (filters as Filter)?.idp,
                onChange: (v: string) => setFilters({ ...filters, idp: v })
              }}
              label={t('activity:filters.IDP')}
              multi
            />
            <DatePicker
              label={t('activity:filters.LAST_AGENT_LOGIN')}
              input={{
                value: (filters as Filter)?.lastVpnLogin,
                onChange: (v: string) =>
                  setFilters({ ...filters, lastVpnLogin: v })
              }}
              trigerResetDatePicker={triggerResetDatePicker}
            />
            <DatePicker
              label={t('activity:filters.LAST_PLATEFORM_LOGIN')}
              input={{
                value: (filters as Filter)?.lastAppLogin,
                onChange: (v: string) =>
                  setFilters({ ...filters, lastAppLogin: v })
              }}
              trigerResetDatePicker={triggerResetDatePicker}
            />

            <ClearButton
              onClick={resetAllFilters}
              numberОfFilters={numberОfFilters}
            />
          </>
        )}
      </div>

      {teamData?.supportWidgetsData && hasCustomerTeamReadAccess && (
        <Widgets data={teamData?.supportWidgetsData} />
      )}
      <DataGrid
        getRowId={entry => entry.userid}
        autoHeight
        lazyLoading
        loading={loaderTable}
        tableConfig={tableConfigDisplay}
        numOfRowsOptions={numOfRowsOptions}
        classes={{ container: styles.row }}
        getRowHeight={getRowHeight}
        tableData={{
          page: teamData?.page || 0,
          totalPages: teamData?.totalPages || 0,
          totalRecords: teamData?.totalRecords || 0,
          data: tableData || []
        }}
        detailPanelField="devicesInfo"
        detailPanelProps={{
          getRowId: entry => entry.deviceId,
          tableConfig: masterDetailConfig
        }}
        initialState={{
          pinnedColumns: {
            left: [
              GridSpecialColumnFields.DETAIL_PANEL_TOGGLE,
              tableConfigDisplay[0].field
            ],
            right: [tableConfigDisplay[tableConfigDisplay.length - 1].field]
          }
        }}
        paginationProps={{
          page: (filters as Filter)?.currentPage - 1,
          count: teamData?.totalRecords || 0,
          rowsPerPage: numOfRowsOptions.find(
            (item: { name: number; selected: boolean }) =>
              item.selected === true
          )!.name,
          onChangePage: (event, page) => {
            setFilter({ ...filter, currentPage: page + 1 });
          },
          hideNavigation: !teamData?.totalRecords,
          hideRowCount: !teamData?.totalRecords
        }}
        toolbarProps={{
          title1: t('activity:platform.TEAM'),
          title2: `(${teamData?.totalRecords || 0})`,
          exportButtonProps: {
            onClick: hasAccess([resources.CUSTOMERS, access.EXPORT])
              ? () => {
                  onChangeExportButton(exportConfigTeam, tableConfigDisplay);
                }
              : undefined,
            disabled: !teamData?.totalRecords
          },
          exportTooltipText: t('activity:cloudManagementLogs.EXPORT'),
          tableSettingsTooltipText: t(
            'activity:cloudManagementLogs.TABLE_SETTINGS'
          ),
          rowSelectionTooltipText: t(
            'activity:cloudManagementLogs.QUANTITY_OF_ITEMS'
          ),
          tableSettingsListItems: tableSettingsDisplay,
          rowSelectionChanged,
          tableSettingsChanged,
          handleTableSettingsMenuItemClick,
          handleSearchChangeCallback,
          onChangeNumOfRows: handleChangeNumOfRows,
          onSortEnd
        }}
        rowCount={teamData?.totalRecords || 0}
        onSortModelChange={a => {
          handleSortable(a[0].field);
        }}
        sortingMode={'server'}
        paginationMode={'server'}
      />
    </>
  );
};

PlatformTeam.propTypes = {
  customerId: PropTypes.string
};

export default PlatformTeam;
